import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Live Audit Feed hook — powers both the dockable Live Operations Console
 * and the `/audit/live` full-page workspace.
 *
 * Behavior
 * --------
 * 1. On mount (or reconnect) hydrates the most recent N events from
 *    `/api/audit/live/hydrate` — client transitions immediately into live
 *    mode via a WebSocket to `/api/ws/audit`.
 * 2. New events are prepended and de-duped by `ledger_id`. Buffer capped at
 *    `capacity` (default 500) — older rows are trimmed.
 * 3. While paused, events accumulate in a `bufferedDuringPause` counter so
 *    the operator can resume with a summary.
 * 4. Connection lifecycle is exposed as `status`:
 *    `connecting | live | reconnecting | offline | forbidden`.
 * 5. Reconnect uses exponential backoff (1s → 30s).
 * 6. Consumer is responsible for permission — the hook is inert unless a
 *    user is authenticated. WS auth is gated server-side against `audit:read`.
 */
const CAPACITY_DEFAULT = 500;
const HYDRATE_LIMIT = 100;

const ALL_ROLES = ["system_admin", "compliance_officer", "auditor", "security_officer", "executive_viewer"];

function hasAuditRead(role) {
  return ALL_ROLES.includes(role);
}

export function useLiveAuditFeed({ capacity = CAPACITY_DEFAULT, enabled = true } = {}) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("connecting");
  const [paused, setPaused] = useState(false);
  const [bufferedDuringPause, setBufferedDuringPause] = useState(0);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const eventsRef = useRef([]);
  const seenRef = useRef(new Set());
  const wsRef = useRef(null);
  const pausedRef = useRef(false);
  const backoffRef = useRef(1000);
  const cancelledRef = useRef(false);
  const pausedBufferRef = useRef(0);

  const authorized = !!user && hasAuditRead(user.role);
  const active = !!user && enabled && authorized;

  // ---- helpers ---------------------------------------------------------
  const commit = useCallback((next) => {
    eventsRef.current = next;
    setEvents(next);
  }, []);

  const acceptEvent = useCallback((evt) => {
    if (!evt || !evt.ledger_id) return;
    if (seenRef.current.has(evt.ledger_id)) return;
    if (pausedRef.current) {
      pausedBufferRef.current += 1;
      setBufferedDuringPause(pausedBufferRef.current);
      return;
    }
    seenRef.current.add(evt.ledger_id);
    const next = [evt, ...eventsRef.current];
    if (next.length > capacity) {
      const removed = next.splice(capacity);
      for (const r of removed) seenRef.current.delete(r.ledger_id);
    }
    commit(next);
  }, [capacity, commit]);

  const hydrate = useCallback(async () => {
    try {
      const { data } = await api.get("/audit/live/hydrate", { params: { limit: HYDRATE_LIMIT } });
      const items = data.items || [];
      seenRef.current = new Set(items.map((i) => i.ledger_id));
      commit(items.slice(0, capacity));
    } catch {
      // hydrate is best-effort; the stream will still keep us informed
    }
  }, [capacity, commit]);

  const refresh = useCallback(async () => {
    await hydrate();
  }, [hydrate]);

  // ---- websocket lifecycle --------------------------------------------
  useEffect(() => {
    if (!active) { setStatus(authorized ? "connecting" : "forbidden"); return; }
    cancelledRef.current = false;
    backoffRef.current = 1000;
    let warnedOnError = false;  // rate-limit console.warn to first occurrence per connect cycle

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace(/^http/, "ws") + "/api/ws/audit";

    let ws;

    const connect = async () => {
      if (cancelledRef.current) return;
      setStatus((s) => (s === "live" ? "reconnecting" : "connecting"));
      await hydrate();
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelledRef.current) return;
        backoffRef.current = 1000;
        warnedOnError = false;  // allow one warn per fresh session
        setReconnectAttempts(0);
        setStatus("live");
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.kind === "audit_event" && msg.event) acceptEvent(msg.event);
      };
      ws.onerror = (ev) => { if (!warnedOnError) { console.warn("[live-audit] ws error, will reconnect", ev); warnedOnError = true; } try { ws.close(); } catch { /* close-on-error may already be closing */ } };
      ws.onclose = (ev) => {
        wsRef.current = null;
        if (cancelledRef.current) return;
        if (ev.code === 1008) { setStatus("forbidden"); return; }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      setStatus("reconnecting");
      const delay = Math.min(backoffRef.current, 30_000);
      backoffRef.current = Math.min(delay * 2, 30_000);
      setReconnectAttempts((n) => n + 1);
      setTimeout(connect, delay);
    };

    connect();
    return () => {
      cancelledRef.current = true;
      try { wsRef.current?.close(); } catch (e) { console.warn("[live-audit] cleanup close failed", e); }
      wsRef.current = null;
    };
  }, [active, authorized, acceptEvent, hydrate]);

  // ---- pause / resume --------------------------------------------------
  const pause = useCallback(() => {
    pausedRef.current = true;
    pausedBufferRef.current = 0;
    setBufferedDuringPause(0);
    setPaused(true);
  }, []);

  const resume = useCallback(async () => {
    pausedRef.current = false;
    setPaused(false);
    // Re-hydrate to pick up any events dropped while paused (up to hydrate limit).
    await hydrate();
    pausedBufferRef.current = 0;
    setBufferedDuringPause(0);
  }, [hydrate]);

  const clear = useCallback(() => {
    seenRef.current = new Set();
    commit([]);
  }, [commit]);

  return useMemo(() => ({
    events,
    status,
    paused,
    pause,
    resume,
    clear,
    refresh,
    bufferedDuringPause,
    reconnectAttempts,
    authorized,
  }), [events, status, paused, pause, resume, clear, refresh, bufferedDuringPause, reconnectAttempts, authorized]);
}
